// src/layouts/ProfileLayout.jsx
import { useEffect, useState, useCallback } from 'react';
import { Outlet, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLangNavigate } from '../../hooks/useLangNavigate';
import { useAuth } from '../../context/UseAuth';
import api from '../../services/api';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faExclamationCircle,
  faUserSlash,
  faHome,
} from '@fortawesome/free-solid-svg-icons';
import { LangLink } from '../common/LangLink';

function ProfileLayout() {
  const { t } = useTranslation();
  const { username: usernameFromParams } = useParams();
  const { user: currentUser, loading: authLoading } = useAuth();
  const [profileDataToDisplay, setProfileDataToDisplay] = useState(null);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [profileError, setProfileError] = useState(null);
  const navigate = useLangNavigate();

  const effectiveUsername = usernameFromParams;

  // Determinar si es perfil propio
  const isOwnProfile =
    !!currentUser &&
    currentUser.username.toLowerCase() === effectiveUsername?.toLowerCase();

  /**
   * Memoizamos el fetch para que no se recree en cada render
   */
  const fetchProfileData = useCallback(async () => {
    if (!effectiveUsername) {
      setProfileError(t('profile.errors.undetermined'));
      setLoadingProfile(false);
      return;
    }

    const endpoint = isOwnProfile
      ? '/users/profile'
      : `/users/${effectiveUsername}`;

    setLoadingProfile(true);
    setProfileError(null);

    try {
      const response = await api.get(endpoint);
      setProfileDataToDisplay(response.data);
    } catch (err) {
      if (err.response?.status === 404) {
        navigate('/not-found', { replace: true });
      } else if (err.response?.status === 403) {
        setProfileError(t('profile.errors.private'));
      } else {
        setProfileError(t('profile.errors.load_failed'));
      }
    } finally {
      setLoadingProfile(false);
    }
  }, [effectiveUsername, isOwnProfile, t]);

  /**
   * Se dispara el fetch cuando cambia el username o authLoading termina
   */
  useEffect(() => {
    if (authLoading) return; // esperamos a que cargue auth
    fetchProfileData();
  }, [authLoading, fetchProfileData]);

  const isLoading = loadingProfile || authLoading;

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mb-4"></div>
        <p className="text-neutral-600 dark:text-neutral-300 text-lg">
          {t('profile.loading')}
        </p>
      </div>
    );
  }

  if (profileError) {
    return (
      <div className="flex flex-col items-center justify-center h-screen p-4 text-center">
        <FontAwesomeIcon
          icon={faExclamationCircle}
          className="text-red-500 text-4xl mb-4"
        />
        <p className="text-red-600 dark:text-red-400 text-lg mb-6">
          {profileError}
        </p>
        <LangLink to="/" className="btn-primary flex items-center gap-2">
          <FontAwesomeIcon icon={faHome} /> {t('profile.actions.go_home')}
        </LangLink>
      </div>
    );
  }

  if (!profileDataToDisplay) {
    return (
      <div className="flex flex-col items-center justify-center h-screen p-4 text-center">
        <FontAwesomeIcon
          icon={faUserSlash}
          className="text-neutral-400 dark:text-neutral-500 text-4xl mb-4"
        />
        <p className="text-neutral-600 dark:text-neutral-300 text-lg mb-6">
          {t('profile.errors.unavailable')}
        </p>
        <LangLink to="/" className="btn-primary flex items-center gap-2">
          <FontAwesomeIcon icon={faHome} /> {t('profile.actions.go_home')}
        </LangLink>
      </div>
    );
  }

  return (
    <div className="profile-layout min-h-screen bg-neutral-50 dark:bg-neutral-900 w-full">
      <div className="w-full pt-4 md:pt-8">
        <main className="w-full">
          <Outlet
            context={{
              profileUserFromLayout: profileDataToDisplay,
              isOwnProfileFromLayout: isOwnProfile,
            }}
          />
        </main>
      </div>
    </div>
  );
}

export default ProfileLayout;
